import { Alert } from '@synoptic/ui-kit/alert/alert.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { CreditCardIcon } from '@synoptic/ui-kit/icons/react/credit-card.js';
import { LockIcon } from '@synoptic/ui-kit/icons/react/lock.js';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.js';
import clsx from 'clsx';
import { ComponentPropsWithRef, useState } from 'react';
import { Form, useNavigation } from 'react-router';
import {
  CustomTargetsDataFragmentFragment,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql';
import { PriceStepperSection } from '@/components/streams/payment-options/price-stepper-section.tsx';
import { getInitialTargetsCount } from '@/components/streams/payment-options/price-stepper-utils.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import {
  formatIntervalFull,
  formatIntervalShort,
  getMonthlyPrice,
  isYearlyInterval,
} from '@/utils/paid-data.ts';
import { ApplyLearningPoints } from './apply-learning-points';
import { getDefaultPriceId } from './get-default-price-id.ts';
import { PaymentOption } from './payment-options-enum';
import {
  priceContainer,
  priceMain,
  priceSubInfo,
} from './price-stepper-section.css.ts';
import {
  paymentOptionCallout,
  paymentOptionDiscountBadge,
  paymentOptionsAlertContent,
  paymentOptionsAlertDescription,
  paymentOptionsAlertTitle,
  paymentOptionsForm,
  paymentOptionsSubmit,
  paymentOptionsTabContent,
  paymentOptionsTabContentFooter,
  paymentOptionSelectTriggerContainer,
  paymentOptionSelectTrigger,
} from './payment-options.css';

const formatPriceAmount = (price: StripePriceFragmentFragment): string => {
  const dollars = convertCentsToDollars(price.amount);
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

const getDiscountPercent = (
  price: StripePriceFragmentFragment,
  monthlyAmount: number,
): number | null => {
  const months = isYearlyInterval(price.interval, price.intervalCount)
    ? 12
    : price.intervalCount;
  if (months <= 1) return null;
  const fullPrice = monthlyAmount * months;
  if (price.amount >= fullPrice) return null;
  return Math.floor(((fullPrice - price.amount) / fullPrice) * 100);
};

export const PaymentOptionsContent = ({
  className,
  ref,
  onSubmit,
  customTargets,
  prices = [],
  action,
  couponId,
  discountAmountCents,
  ...props
}: {
  onSubmit?: (
    event: React.FormEvent<HTMLFormElement>,
    values: { paymentOption: PaymentOption },
  ) => void;
  customTargets?: CustomTargetsDataFragmentFragment;
  prices?: StripePriceFragmentFragment[];
  couponId?: string;
  discountAmountCents?: number;
} & Omit<ComponentPropsWithRef<typeof Form>, 'onSubmit'>) => {
  const { state: navState } = useNavigation();

  const [selectedPriceId, setSelectedPriceId] = useState<string>(
    getDefaultPriceId(prices) ?? '',
  );

  const [targetsCount, setTargetsCount] = useState(() =>
    customTargets ? getInitialTargetsCount(customTargets) : 0,
  );

  const selectedPrice = prices.find((p) => p.id === selectedPriceId);
  const steps = customTargets ? targetsCount / customTargets.stepSize : 0;
  const monthlyAmount = getMonthlyPrice(prices);

  const priceSelectItems = prices.map((p) => {
    const discount =
      monthlyAmount != null ? getDiscountPercent(p, monthlyAmount) : null;
    return {
      value: p.id,
      content: (
        <>
          {formatIntervalFull(p.interval, p.intervalCount)}
          {discount != null && (
            <span className={paymentOptionDiscountBadge}>-{discount}%</span>
          )}
        </>
      ),
    };
  });

  return (
    <Form
      ref={ref}
      action={action}
      className={clsx(paymentOptionsForm, className)}
      onSubmit={(e) => {
        onSubmit?.(e, { paymentOption: PaymentOption.Card });
      }}
      {...props}
      method="POST"
    >
      {customTargets && selectedPrice ? (
        <PriceStepperSection
          customTargets={customTargets}
          selectedPrice={selectedPrice}
          discountAmountCents={couponId ? discountAmountCents : undefined}
          targetsCount={targetsCount}
          setTargetsCount={setTargetsCount}
        />
      ) : null}

      <input type="hidden" name="paymentOption" value={PaymentOption.Card} />
      <input type="hidden" name="priceId" value={selectedPriceId} />
      {customTargets ? (
        <input type="hidden" name="steps" value={steps} />
      ) : null}
      {couponId && <input type="hidden" name="couponId" value={couponId} />}

      <div className={paymentOptionsTabContent}>
        <Alert
          icon={<CreditCardIcon />}
          size="small"
          className={paymentOptionCallout}
          primaryAction={
            <Select value={selectedPriceId} onValueChange={setSelectedPriceId}>
              <SelectTrigger
                variant="outline"
                containerClassName={paymentOptionSelectTriggerContainer}
                className={paymentOptionSelectTrigger}
                triggerText={
                  selectedPrice
                    ? formatIntervalFull(
                        selectedPrice.interval,
                        selectedPrice.intervalCount,
                      )
                    : ''
                }
              />
              <SelectContent items={priceSelectItems} />
            </Select>
          }
        >
          <div className={paymentOptionsAlertContent}>
            <span className={paymentOptionsAlertTitle}>
              Recurring subscription
            </span>
            <span className={paymentOptionsAlertDescription}>
              Your payment will be securely processed via Stripe, ensuring
              uninterrupted access to premium content.
            </span>
          </div>
        </Alert>

        {selectedPrice && !customTargets && (
          <div className={priceContainer}>
            <span className={priceMain}>
              US{formatPriceAmount(selectedPrice)}
            </span>
            <span className={priceSubInfo}>
              per <br />{' '}
              {formatIntervalShort(
                selectedPrice.interval,
                selectedPrice.intervalCount,
              )}
            </span>
          </div>
        )}

        <ApplyLearningPoints />

        <Button
          startIcon={<LockIcon />}
          size="large"
          className={paymentOptionsSubmit}
          fullWidth
          type="submit"
          loading={navState === 'submitting'}
          disabled={prices.length === 0}
        >
          Subscribe
        </Button>

        <span className={paymentOptionsTabContentFooter}>
          Auto-renewal enabled. Cancel anytime.
        </span>
      </div>
    </Form>
  );
};
